/**
 * 文件说明：保存“从列表 slot link 按钮发起连线”的一次性意图。
 *
 * React Flow 原生 onConnect 只知道 source/target，不知道用户是从节点默认手柄发起，
 * 还是从某个 slot 的 link 按钮发起。这里用一个极小的模块级意图记录补齐这份上下文，
 * 在连接成功时消费掉，连接取消时清理掉。
 */
import { proxy } from 'valtio'
import { useSnapshot } from 'valtio/react'

export interface SlotReferenceConnectIntent {
  /**
   * 本次拖拽预览线的临时起点，使用浏览器 client 坐标。
   *
   * ReactFlow connection line 实际绘制在画布坐标系里，渲染时会再通过
   * screenToFlowPosition 转换一次。
   * 只服务拖拽中的 connection line；真正落库的 edge 仍然连接节点级 handle，
   * 不会把 slot 按钮坐标写入持久数据。
   */
  previewOrigin?: {
    x: number
    y: number
  }
  /**
   * previewOrigin 在拖拽开始时对应的 React Flow 画布坐标。
   *
   * 拖拽过程中画布可能滚动或平移，client 坐标会随 viewport 重新映射而漂移；
   * 因此一旦画布层能拿到 screenToFlowPosition，就把起点固化成 flow 坐标。
   */
  previewOriginFlowPosition?: {
    x: number
    y: number
  }
  sourceNodeId: string
  sourceKind: 'image' | 'text' | 'video' | 'audio' | 'gallery' | 'text-list' | 'audio-list' | 'video-list'
  /**
   * 本次引用涉及的 slot 下标列表。
   *
   * 单格按钮只会引用一个 slot；批量工具栏会一次引用多个已选 slot。
   * 单格场景也统一使用长度为 1 的数组，避免直接连线和菜单创建走两套字段。
   */
  slotIndexes: readonly number[]
}

export interface SlotReferenceGuideRequest {
  id: number
  intent: SlotReferenceConnectIntent
}

let pendingIntent: SlotReferenceConnectIntent | null = null
let nextGuideRequestId = 0

const slotReferenceGuideRequestStore = proxy<{
  request: SlotReferenceGuideRequest | null
}>({
  request: null,
})

/**
 * 记录本次 slot 引用连线意图。
 *
 * @param intent source 节点、当前批次内 slot 下标，以及该 slot 对外表现的单项类型
 */
export function beginSlotReferenceConnectIntent(intent: SlotReferenceConnectIntent) {
  pendingIntent = intent
}

/**
 * 请求画布打开 slot 引用的下游创建菜单。
 *
 * SlotLinkButton 位于 entities 层，不能直接调用 widgets/flow-canvas 的菜单逻辑；
 * 这里沿用项目已有的 intent store 风格，把一次性菜单请求交给画布层消费。
 *
 * @param intent source 节点、slot 下标和当前 slot 对外表现的资源类型
 */
export function requestOpenSlotReferenceGuide(intent: SlotReferenceConnectIntent) {
  pendingIntent = intent
  nextGuideRequestId += 1
  slotReferenceGuideRequestStore.request = {
    id: nextGuideRequestId,
    intent,
  }
}

/**
 * 订阅 slot 点击打开创建菜单的一次性请求。
 *
 * 画布层在 request 变化时消费并清理，避免 entities 反向依赖 widgets。
 */
export function useSlotReferenceGuideRequest() {
  return useSnapshot(slotReferenceGuideRequestStore).request
}

/**
 * 清理已消费的 slot 创建菜单请求。
 *
 * @param requestId 当前消费的请求 ID，避免异步 effect 清掉后一次点击请求
 */
export function clearSlotReferenceGuideRequest(requestId: number) {
  if (slotReferenceGuideRequestStore.request?.id !== requestId) {
    return
  }
  slotReferenceGuideRequestStore.request = null
}

/**
 * 读取当前未消费的 slot 引用连线意图。
 *
 * @returns 当前意图；没有从 slot link 发起连线时返回 null
 */
export function peekSlotReferenceConnectIntent() {
  return pendingIntent
}

/**
 * 缓存 slot 引用拖拽起点的画布坐标。
 *
 * @param position slot 按钮中心点对应的 flow 坐标
 */
export function cacheSlotReferencePreviewOriginFlowPosition(position: { x: number; y: number }) {
  if (!pendingIntent) {
    return
  }

  pendingIntent.previewOriginFlowPosition = position
}

/**
 * 在连接成功时消费 slot 引用连线意图。
 *
 * @param sourceNodeId 当前 onConnect 的 source 节点 ID
 * @returns source 匹配时返回意图并清空；不匹配时返回 null，避免污染普通连线
 */
export function consumeSlotReferenceConnectIntent(sourceNodeId: string) {
  if (pendingIntent?.sourceNodeId !== sourceNodeId) {
    return null
  }

  const intent = pendingIntent
  pendingIntent = null
  return intent
}

/**
 * 清理未完成的 slot 引用连线意图。
 *
 * 连接取消、落到非法目标或打开空白引导菜单时都应调用，避免下一次普通连线误用旧 slot。
 */
export function clearSlotReferenceConnectIntent() {
  pendingIntent = null
}
